import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ReturnDto, ReturnStatus } from '@ecom/contracts';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { EMPTY_RETURN_FILTERS, useReturnsStore } from '@/stores/returns.store';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count, date, money } from '@/lib/format';
import {
  RETURN_REASONS,
  RETURN_STATUSES,
  returnReasonKey,
  returnStatusKey,
  returnStatusTone,
  returnSteps,
  type ReturnStep,
} from '@/lib/returns';
import { ReturnIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, TableScroll, Table, Tabs, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Input, Pill, Select, TableSkeleton } from '@/components/ui';
import { ReturnSheet } from './returns/return-sheet';
import { RefundSheet } from './returns/refund-sheet';
import { StepSheet } from './returns/step-sheet';

/**
 * The list's views — tabs over the status filter, one per step that waits on
 * the store, then the closed ones. Each is just a set of statuses, so a view is
 * a filter someone can still see and change under "filters".
 */
const RETURN_VIEWS: { id: string; label: MessageKey; statuses: ReturnStatus[] }[] = [
  { id: 'all', label: 'returnView_all', statuses: [] },
  { id: 'requested', label: 'returnStatusREQUESTED', statuses: ['REQUESTED'] },
  { id: 'approved', label: 'returnStatusAPPROVED', statuses: ['APPROVED'] },
  { id: 'received', label: 'returnStatusRECEIVED', statuses: ['RECEIVED'] },
  { id: 'closed', label: 'returnView_closed', statuses: ['REFUNDED', 'REJECTED', 'CANCELLED'] },
];

/** The view a status filter is exactly, or 'custom' when it is a hand-made mix. */
function viewOf(statuses: readonly string[]): string {
  const chosen = [...statuses].sort().join();
  return RETURN_VIEWS.find((view) => [...view.statuses].sort().join() === chosen)?.id ?? 'custom';
}

const stop = (event: MouseEvent) => event.stopPropagation();

/**
 * Returns.
 *
 * The only screen that moves money. Each row offers exactly the next legal
 * step, because the state machine refuses to refund before approval — showing
 * all four buttons at once would just teach an operator which ones fail. A row
 * opens the whole return beside the list, with every step it can take; each
 * step asks for what it needs in a panel of its own, and the return is back
 * in view once it is done.
 */
export function ReturnsPage() {
  const {
    items,
    loading,
    loadingMore,
    saving,
    error,
    nextCursor,
    filters,
    total,
    load,
    loadMore,
    open,
    close,
    loadOrder,
    loadProviderNames,
    clearActionError,
  } = useReturnsStore();
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  const mayReadOrders = useAuthStore((state) => can(state.user, 'orders:read'));
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState<{ step: ReturnStep; row: ReturnDto } | null>(null);

  // A customer's page links here with their email as the search. It is run
  // once; after that the filters are the operator's, and the address lets go.
  const linkedSearch = useRef(params.get('search'));
  useEffect(() => {
    const search = linkedSearch.current;
    if (search === null) {
      void load();
      return;
    }
    linkedSearch.current = null;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('search');
        return next;
      },
      { replace: true },
    );
    void load({ ...EMPTY_RETURN_FILTERS, search });
  }, [load, locale, setParams]);

  // The open return lives in the address, so a customer's page — or a
  // colleague — can link straight to it.
  const openId = params.get('open');
  useEffect(() => {
    if (openId) void open(openId, mayReadOrders);
    else close();
  }, [openId, open, close, mayReadOrders]);

  // The owner reads where a payment came from by the provider's own name.
  useEffect(() => {
    if (openId && isOwner) void loadProviderNames();
  }, [openId, isOwner, loadProviderNames]);

  const setOpen = (id: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('open', id);
        else next.delete('open');
        return next;
      },
      { replace: true },
    );

  function startStep(next: ReturnStep, row: ReturnDto) {
    clearActionError();
    if (next === 'refund') {
      // Which way the money goes back is on the order, and the provider's name in its catalogue.
      void loadOrder(row.orderId);
      void loadProviderNames();
    }
    setStep({ step: next, row });
  }

  return (
    <Page title={t('returns')} subtitle={t('returnsSubtitle')} fill>
      <ListCard>
        <Tabs
          className="px-2"
          label={t('returns')}
          value={viewOf(filters.statuses)}
          onChange={(id) => void load({ statuses: [...(RETURN_VIEWS.find((view) => view.id === id)?.statuses ?? [])] })}
          items={RETURN_VIEWS.map((view) => ({ id: view.id, label: t(view.label) }))}
        />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchReturnsHint')}
            total={total ?? undefined}
            shown={items.length}
            chips={[
              ...filters.statuses.map((value) => ({
                key: `s-${value}`,
                label: t(returnStatusKey(value)),
                onRemove: () => void load({ statuses: filters.statuses.filter((entry) => entry !== value) }),
              })),
              ...(filters.reason
                ? [{ key: 'reason', label: t(returnReasonKey(filters.reason)), onRemove: () => void load({ reason: '' }) }]
                : []),
              ...(filters.from
                ? [{ key: 'from', label: `${t('filterFrom')}: ${filters.from}`, onRemove: () => void load({ from: '' }) }]
                : []),
              ...(filters.to
                ? [{ key: 'to', label: `${t('filterTo')}: ${filters.to}`, onRemove: () => void load({ to: '' }) }]
                : []),
            ]}
            onClearAll={() => void load(EMPTY_RETURN_FILTERS)}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <FilterField label={t('filterStatus')}>
                <ToggleGroup
                  options={RETURN_STATUSES.map((value) => ({ value, label: t(returnStatusKey(value)) }))}
                  selected={filters.statuses}
                  onChange={(statuses) => void load({ statuses })}
                />
              </FilterField>

              <FilterField label={t('filterReason')} htmlFor="r-reason">
                <Select
                  id="r-reason"
                  value={filters.reason}
                  onChange={(event) => void load({ reason: event.target.value })}
                >
                  <option value="">{t('anyValue')}</option>
                  {RETURN_REASONS.map((value) => (
                    <option key={value} value={value}>
                      {t(returnReasonKey(value))}
                    </option>
                  ))}
                </Select>
              </FilterField>

              <div className="grid grid-cols-2 gap-2">
                <FilterField label={t('filterFrom')} htmlFor="r-from">
                  <Input
                    id="r-from"
                    type="date"
                    dir="ltr"
                    value={filters.from}
                    onChange={(event) => void load({ from: event.target.value })}
                  />
                </FilterField>
                <FilterField label={t('filterTo')} htmlFor="r-to">
                  <Input
                    id="r-to"
                    type="date"
                    dir="ltr"
                    value={filters.to}
                    onChange={(event) => void load({ to: event.target.value })}
                  />
                </FilterField>
              </div>
            </div>
          </FilterBar>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noReturns')} body={t('noReturnsHint')} icon={<ReturnIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('returnNumber')}</Th>
                  <Th>{t('product')}</Th>
                  <Th>{t('status')}</Th>
                  <Th align="end">{t('amount')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  // The whole row opens the return; the numbers stay real links
                  // for a middle-click or a copied address.
                  <Tr
                    key={row.id}
                    onClick={() => setOpen(row.id)}
                    className={openId === row.id ? 'bg-surface-sunken' : undefined}
                  >
                    <Td>
                      <Link
                        to={`?open=${row.id}`}
                        replace
                        onClick={stop}
                        dir="ltr"
                        data-numeric
                        className="block w-fit font-medium text-brand-700 hover:underline"
                      >
                        {row.number}
                      </Link>
                      <span className="block text-xs text-ink-muted">
                        <Link
                          to={`/orders/${row.orderId}`}
                          onClick={stop}
                          dir="ltr"
                          data-numeric
                          className="hover:text-brand-700 hover:underline"
                        >
                          {row.orderNumber}
                        </Link>
                        {' · '}
                        {date(row.requestedAt, locale)}
                      </span>
                    </Td>
                    <Td>
                      <span dir="auto" className="block text-start text-ink">{row.productName}</span>
                      <span className="block text-xs text-ink-muted">
                        {t('returnQty', { count: count(row.quantity, locale) })} · {t(returnReasonKey(row.reason))}
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={returnStatusTone(row.status)}>{t(returnStatusKey(row.status))}</Pill>
                    </Td>
                    <Td align="end" data-numeric className="font-medium text-ink">
                      {money(row.amount, locale)}
                      {row.refundedAmount > 0 ? (
                        <span className="block text-xs font-normal text-success">
                          {t('refunded')} {money(row.refundedAmount, locale)}
                        </span>
                      ) : null}
                    </Td>
                    <Td align="end" onClick={stop}>
                      <RowSteps row={row} isOwner={isOwner} saving={saving} onStep={startStep} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {/* One panel at a time: a step opened from the return takes its place, and the return comes back after. */}
      <ReturnSheet
        open={openId !== null && step === null}
        isOwner={isOwner}
        mayReadOrders={mayReadOrders}
        onClose={() => setOpen(null)}
        onStep={startStep}
      />

      {step && step.step !== 'refund' ? (
        <StepSheet key={`${step.step}-${step.row.id}`} step={step.step} row={step.row} onClose={() => setStep(null)} />
      ) : null}

      {step?.step === 'refund' ? (
        <RefundSheet key={step.row.id} row={step.row} onClose={() => setStep(null)} />
      ) : null}
    </Page>
  );
}

/**
 * The row's own buttons: the next step only — both answers while it is a
 * request. Everything else a return can do is in its panel.
 */
function RowSteps({
  row,
  isOwner,
  saving,
  onStep,
}: {
  row: ReturnDto;
  isOwner: boolean;
  saving: boolean;
  onStep: (step: ReturnStep, row: ReturnDto) => void;
}) {
  const t = useT();
  const next = returnSteps(row.status, true)[0];

  if (row.status === 'REQUESTED') {
    return (
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => onStep('approve', row)} disabled={saving}>
          {t('approve')}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onStep('reject', row)} disabled={saving}>
          {t('reject')}
        </Button>
      </div>
    );
  }

  if (next === 'receive') {
    return (
      <Button variant="secondary" size="sm" onClick={() => onStep('receive', row)} disabled={saving}>
        {t('receive')}
      </Button>
    );
  }

  if (next === 'refund') {
    return isOwner ? (
      <Button size="sm" onClick={() => onStep('refund', row)} disabled={saving}>
        {t('refund')}
      </Button>
    ) : (
      // Staff see why the button is absent rather than wondering whether the screen is broken.
      <span className="text-xs text-ink-muted">{t('ownerOnlyRefund')}</span>
    );
  }

  return null;
}
